import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Heart, Bookmark, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProfilePage({ user, onUserUpdate }) {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const isOwnProfile = !userId || userId === user?.id;
  const profileId = userId || user?.id;

  useEffect(() => {
    if (profileId) {
      fetchProfile();
      fetchUserPosts();
    }
  }, [profileId]);

  const fetchProfile = async () => {
    try {
      if (isOwnProfile) {
        setProfile(user);
      } else {
        const response = await axios.get(`${API}/users/${profileId}`);
        setProfile(response.data);
      }
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/users/${profileId}/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.post(`${API}/upload/avatar`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, avatar: res.data.avatar_url }));
      if (onUserUpdate) onUserUpdate({ ...user, avatar: res.data.avatar_url });
      toast.success('Profile photo updated!');
    } catch { toast.error('Failed to upload photo'); }
    setUploadingAvatar(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 mb-6 peaceful-enter">
        <div className="flex flex-col items-center text-center">
          <div className="relative group">
            <img
              src={profile.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name || 'U')}&background=FF9933&color=fff&size=96`}
              alt={profile.name}
              className="w-24 h-24 rounded-full object-cover border-4 border-orange-100 mb-4"
              data-testid="profile-avatar"
            />
            {isOwnProfile && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  data-testid="avatar-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="absolute bottom-3 right-0 w-8 h-8 bg-[#FF9933] hover:bg-[#E88829] rounded-full flex items-center justify-center shadow-md border-2 border-white transition-all"
                  data-testid="upload-avatar-btn"
                >
                  {uploadingAvatar ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
                </button>
              </>
            )}
          </div>
          <h1 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            {profile.name}
          </h1>
          <div className="flex items-center gap-2 mb-4">
            {profile.role === 'pandit' && (
              <span className="px-3 py-1 bg-[#FF9933] text-white text-sm rounded-full font-medium">Verified Pandit</span>
            )}
            {profile.role === 'creator' && (
              <span className="px-3 py-1 bg-purple-500 text-white text-sm rounded-full font-medium">Creator</span>
            )}
            {profile.role === 'user' && (
              <span className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full">Devotee</span>
            )}
          </div>
          {profile.bio && <p className="text-slate-600 max-w-md">{profile.bio}</p>}
          <p className="text-sm text-slate-500 mt-4">
            Joined {new Date(profile.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Posts */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800 mb-4" style={{ fontFamily: 'Merriweather, serif' }}>
          Posts
        </h2>

        <div className="space-y-6">
          {posts.map((post, index) => (
            <div
              key={post.id}
              data-testid={`profile-post-${index}`}
              className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 peaceful-enter"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Content */}
              <div className="mb-4">
                {post.post_type === 'mantra' && (
                  <div className="bg-orange-50/50 rounded-xl p-6 mb-4 border border-orange-100">
                    <p className="text-lg text-slate-800 text-center mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                      {post.content}
                    </p>
                    {post.mantra_meaning && (
                      <p className="text-sm text-slate-600 text-center italic">Meaning: {post.mantra_meaning}</p>
                    )}
                  </div>
                )}

                {post.post_type === 'text' && (
                  <p className="text-slate-700 leading-relaxed">{post.content}</p>
                )}

                {(post.post_type === 'video' || post.post_type === 'audio') && post.media_url && (
                  <div className="mb-3">
                    <p className="text-slate-700 leading-relaxed mb-3">{post.content}</p>
                    {post.post_type === 'video' && (
                      <div className="aspect-video rounded-xl overflow-hidden bg-slate-100">
                        <iframe
                          src={post.media_url.replace('watch?v=', 'embed/')}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 pt-4 border-t border-stone-100 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Heart className="w-4 h-4" />
                  <span>{post.likes_count} Namaste</span>
                </div>
                <span className="text-slate-400">{new Date(post.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          ))}

          {posts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl">
              <p className="text-slate-600">No posts yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
